/** COCO-17 bone pairs for skeleton drawing. */
export const COCO_BONE_PAIRS = [
  [0, 1],
  [0, 2],
  [1, 3],
  [2, 4],
  [5, 7],
  [7, 9],
  [6, 8],
  [8, 10],
  [5, 6],
  [5, 11],
  [6, 12],
  [11, 12],
  [11, 13],
  [13, 15],
  [12, 14],
  [14, 16],
];

/** MediaPipe Pose 33 connections (face, hands, heels, foot index). */
export const MEDIAPIPE_POSE_BONE_PAIRS = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 7],
  [0, 4],
  [4, 5],
  [5, 6],
  [6, 8],
  [9, 10],
  [11, 12],
  [11, 13],
  [13, 15],
  [15, 17],
  [15, 19],
  [15, 21],
  [17, 19],
  [12, 14],
  [14, 16],
  [16, 18],
  [16, 20],
  [16, 22],
  [18, 20],
  [11, 23],
  [12, 24],
  [23, 24],
  [23, 25],
  [25, 27],
  [27, 29],
  [27, 31],
  [29, 31],
  [24, 26],
  [26, 28],
  [28, 30],
  [28, 32],
  [30, 32],
];

export function overlayJointsForSample(sideSample) {
  if (!sideSample || typeof sideSample !== "object") {
    return { joints: null, bones: COCO_BONE_PAIRS, predicted: null };
  }
  const full = sideSample.joints_full;
  if (Array.isArray(full) && full.length >= 33) {
    return {
      joints: full,
      bones: MEDIAPIPE_POSE_BONE_PAIRS,
      predicted: null,
    };
  }
  return {
    joints: sideSample.joints || null,
    bones: COCO_BONE_PAIRS,
    predicted: sideSample.predicted || null,
  };
}

/**
 * Nearest dual-player sample for playback time (ms).
 * If maxGapMs is set, return null when the nearest sample is farther than that.
 * Prefer sampleNearestInSpan for analyzed ranges.
 */
export function sampleNearest(samples, tMs, maxGapMs = null) {
  if (!Array.isArray(samples) || samples.length === 0) return null;
  let best = samples[0];
  let bestDist = Math.abs((best.t_ms || 0) - tMs);
  for (let i = 1; i < samples.length; i++) {
    const d = Math.abs((samples[i].t_ms || 0) - tMs);
    if (d < bestDist) {
      best = samples[i];
      bestDist = d;
    }
  }
  if (
    maxGapMs != null &&
    Number.isFinite(maxGapMs) &&
    maxGapMs >= 0 &&
    bestDist > maxGapMs
  ) {
    return null;
  }
  return best;
}

/**
 * Pick the pose sample for the current playhead.
 * Uses sample timestamps when available; falls back to frame_index.
 * Inside the sample span always returns a nearest sample (no freeze/hide).
 * Outside the span returns null so the overlay disappears.
 */
export function sampleForPlayback(samples, { tMs, frameIndex = null, padMs = 120 } = {}) {
  if (!Array.isArray(samples) || samples.length === 0) return null;

  const first = samples[0];
  const last = samples[samples.length - 1];
  const t0 = Number(first.t_ms) || 0;
  const t1 = Number(last.t_ms) || t0;

  if (Number.isFinite(tMs)) {
    if (tMs < t0 - padMs || tMs > t1 + padMs) {
      // Still allow frame-index match if timestamps drift vs browser clock.
      if (
        frameIndex == null ||
        first.frame_index == null ||
        last.frame_index == null ||
        frameIndex < first.frame_index - 2 ||
        frameIndex > last.frame_index + 2
      ) {
        return null;
      }
    }
  }

  if (
    frameIndex != null &&
    samples.some((s) => s.frame_index != null)
  ) {
    let best = samples[0];
    let bestDist = Math.abs((best.frame_index ?? 0) - frameIndex);
    for (let i = 1; i < samples.length; i++) {
      const d = Math.abs((samples[i].frame_index ?? 0) - frameIndex);
      if (d < bestDist) {
        best = samples[i];
        bestDist = d;
      }
    }
    return best;
  }

  return sampleNearest(samples, tMs, null);
}

export function formatPoseAngles(angles, title) {
  const head = title ? `${title}\n` : "";
  if (!angles) return `${head}--`;
  const lines = [
    ["L elbow", angles.left_elbow],
    ["R elbow", angles.right_elbow],
    ["L knee", angles.left_knee],
    ["R knee", angles.right_knee],
  ]
    .map(([name, deg]) =>
      deg != null && Number.isFinite(Number(deg))
        ? `${name}: ${Math.round(Number(deg))}°`
        : `${name}: --°`
    )
    .join("\n");
  return `${head}${lines}`;
}

export function formatEta(seconds) {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  const s = Math.round(seconds);
  if (s < 60) return `~${s}s remaining`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `~${m}m ${rem}s remaining`;
}
